import { useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    FileCheck,
    Landmark,
    Wallet,
    CheckCircle2,
    ChevronDown,
    ShieldCheck,
} from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/ScrollReveal";

const STEPS = [
    { n: "01", title: "Je choisis ma formation", text: "Je sélectionne la session qui me convient sur notre site et je remplis le formulaire de pré-inscription." },
    { n: "02", title: "Je reçois mes documents", text: "Cursus Physio vous envoie le programme, la convention et les informations pratiques pour votre dossier." },
    { n: "03", title: "Je dépose ma demande", text: "Vous déposez votre demande sur la plateforme FIF PL ou sur l'Agence DPC au moins 10 jours avant la formation." },
    { n: "04", title: "Je reçois mon accord et je participe", text: "Une fois l'accord de prise en charge reçu, vous participez à la formation. Cursus Physio vous remet l'attestation à la fin." },
];

const DOCUMENTS = [
    "Programme pédagogique détaillé",
    "Convention de formation individuelle",
    "Facture acquittée",
    "Attestation de présence",
    "Feuille d'émargement",
    "Attestation de fin de formation",
];

const FAQ = [
    {
        q: "Qui peut bénéficier du FIF PL ?",
        a: "Les professionnels libéraux cotisant à l'URSSAF, dont les kinésithérapeutes. La prise en charge peut atteindre 750 €/an selon les thèmes prioritaires.",
    },
    {
        q: "Qu'est-ce que le DPC ?",
        a: "Le Développement Professionnel Continu est une obligation triennale. Nos actions DPC sont référencées et permettent de valider votre obligation.",
    },
    {
        q: "Quand faire ma demande de prise en charge ?",
        a: "Au minimum 10 jours ouvrés avant la date de la formation, idéalement dès votre pré-inscription.",
    },
    {
        q: "Que couvrent ces prises en charge ?",
        a: "Elles couvrent tout ou partie des frais pédagogiques (390 €). Les frais annexes (transport, hébergement, repas) ne sont pas couverts.",
    },
    {
        q: "Cursus Physio fait-il les démarches à ma place ?",
        a: "Nous ne pouvons pas déposer la demande à votre place, mais nous fournissons l'ensemble des documents nécessaires et un accompagnement par email ou téléphone.",
    },
];

function FaqItem({ item, idx }) {
    const [open, setOpen] = useState(idx === 0);
    return (
        <div className="border-b border-brand-line" data-testid={`faq-item-${idx}`}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="flex w-full items-center justify-between gap-6 py-6 text-left"
                data-testid={`faq-toggle-${idx}`}
            >
                <span className="font-display text-lg md:text-xl text-brand-ink">{item.q}</span>
                <ChevronDown className={`h-5 w-5 flex-shrink-0 text-brand-orange transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="pb-6 pr-8 text-base leading-relaxed text-brand-ink/75">
                    {item.a}
                </div>
            )}
        </div>
    );
}

export default function PriseEnCharge() {
    return (
        <div data-testid="page-prise-en-charge" className="bg-white">
            {/* Hero */}
            <section className="relative bg-brand-ink text-white pt-40 pb-24 md:pt-48 md:pb-32 grain">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal>
                        <span className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-orange">
                            Prise en charge
                        </span>
                        <h1 className="mt-6 max-w-4xl font-display text-5xl font-normal leading-[1.03] tracking-tightest sm:text-6xl lg:text-7xl">
                            Formez-vous en profitant du <em className="italic text-brand-orange">FIF PL</em> et du{" "}
                            <em className="italic text-brand-orange">DPC</em>.
                        </h1>
                        <p className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed">
                            Toutes nos formations sont éligibles. Nous vous accompagnons dans vos démarches et
                            vous fournissons l'intégralité des documents nécessaires à votre dossier.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Les 2 dispositifs */}
            <section className="py-24 md:py-32 bg-white">
                <div className="mx-auto max-w-7xl px-6 grid gap-8 md:grid-cols-2">
                    <Reveal>
                        <div className="h-full rounded-3xl border border-brand-line bg-brand-sand p-10 shadow-premium" data-testid="block-fif-pl">
                            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                                <Landmark className="h-5 w-5" />
                            </div>
                            <h2 className="mt-6 font-display text-3xl">FIF PL</h2>
                            <p className="mt-2 text-sm uppercase tracking-wider text-brand-smoke">
                                Fonds Interprofessionnel de Formation des Professionnels Libéraux
                            </p>
                            <p className="mt-6 text-base leading-relaxed text-brand-ink/80">
                                Le FIF PL finance les formations des professionnels libéraux cotisant à l'URSSAF.
                                Pour les kinésithérapeutes, le plafond annuel est de <strong>750 €</strong>
                                selon les thèmes prioritaires de la profession.
                            </p>
                            <ul className="mt-6 space-y-2.5 text-sm text-brand-ink/80">
                                <li className="flex items-start gap-2.5"><CheckCircle2 className="mt-0.5 h-4 w-4 text-brand-orange" /> Plafond de 750 €/an/kiné</li>
                                <li className="flex items-start gap-2.5"><CheckCircle2 className="mt-0.5 h-4 w-4 text-brand-orange" /> Demande en ligne sur fifpl.fr</li>
                                <li className="flex items-start gap-2.5"><CheckCircle2 className="mt-0.5 h-4 w-4 text-brand-orange" /> Dossier à déposer avant la formation</li>
                            </ul>
                        </div>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="h-full rounded-3xl border border-brand-line bg-brand-sand p-10 shadow-premium" data-testid="block-dpc">
                            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                                <ShieldCheck className="h-5 w-5" />
                            </div>
                            <h2 className="mt-6 font-display text-3xl">DPC</h2>
                            <p className="mt-2 text-sm uppercase tracking-wider text-brand-smoke">
                                Développement Professionnel Continu
                            </p>
                            <p className="mt-6 text-base leading-relaxed text-brand-ink/80">
                                Le DPC est une obligation triennale pour chaque professionnel de santé. Nos
                                programmes référencés permettent à la fois de vous former et de valider votre obligation.
                            </p>
                            <ul className="mt-6 space-y-2.5 text-sm text-brand-ink/80">
                                <li className="flex items-start gap-2.5"><CheckCircle2 className="mt-0.5 h-4 w-4 text-brand-orange" /> Forfait annuel 450 €/kiné</li>
                                <li className="flex items-start gap-2.5"><CheckCircle2 className="mt-0.5 h-4 w-4 text-brand-orange" /> Validation sur mondpc.fr</li>
                                <li className="flex items-start gap-2.5"><CheckCircle2 className="mt-0.5 h-4 w-4 text-brand-orange" /> Traçabilité assurée par Cursus Physio</li>
                            </ul>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Comment procéder */}
            <section className="py-24 md:py-32 bg-brand-sand">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal className="max-w-3xl">
                        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange">
                            Comment procéder
                        </span>
                        <h2 className="mt-6 font-display text-4xl sm:text-5xl leading-tight">
                            Une démarche <em className="italic text-brand-smoke">en 4 étapes</em>.
                        </h2>
                    </Reveal>
                    <StaggerGroup className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
                        {STEPS.map((step) => (
                            <StaggerItem key={step.n}>
                                <div className="h-full rounded-3xl border border-brand-line bg-white p-8 shadow-premium" data-testid={`step-${step.n}`}>
                                    <div className="font-display text-4xl text-brand-orange">{step.n}</div>
                                    <h3 className="mt-6 font-display text-xl leading-tight text-brand-ink">{step.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-brand-ink/75">{step.text}</p>
                                </div>
                            </StaggerItem>
                        ))}
                    </StaggerGroup>
                </div>
            </section>

            {/* Documents fournis */}
            <section className="py-24 md:py-32 bg-brand-ink text-white grain">
                <div className="mx-auto max-w-7xl px-6 grid gap-12 lg:grid-cols-12 lg:items-center">
                    <Reveal className="lg:col-span-5">
                        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange">
                            Documents fournis
                        </span>
                        <h2 className="mt-6 font-display text-4xl sm:text-5xl leading-tight">
                            Tous les documents, <br />
                            <em className="italic text-white/60">tous les justificatifs</em>.
                        </h2>
                        <p className="mt-8 max-w-md text-base leading-relaxed text-white/70">
                            Pour chaque session, Cursus Physio vous transmet l'ensemble des documents
                            nécessaires à votre prise en charge et à la constitution de votre dossier.
                        </p>
                    </Reveal>
                    <div className="lg:col-span-7">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {DOCUMENTS.map((doc) => (
                                <div key={doc} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange/15 text-brand-orange">
                                        <FileCheck className="h-5 w-5" />
                                    </div>
                                    <div className="font-medium">{doc}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-24 md:py-32 bg-white">
                <div className="mx-auto max-w-5xl px-6">
                    <Reveal>
                        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange">FAQ</span>
                        <h2 className="mt-6 font-display text-4xl sm:text-5xl leading-tight">
                            Questions <em className="italic text-brand-smoke">fréquentes</em>.
                        </h2>
                    </Reveal>
                    <div className="mt-14">
                        {FAQ.map((item, i) => (
                            <FaqItem key={item.q} item={item} idx={i} />
                        ))}
                    </div>

                    <div className="mt-16 rounded-3xl bg-brand-sand p-10 text-center">
                        <Wallet className="mx-auto h-7 w-7 text-brand-orange" />
                        <h3 className="mt-4 font-display text-2xl">Une question sur votre prise en charge ?</h3>
                        <p className="mt-3 text-brand-ink/75">Nous vous accompagnons, par email ou par téléphone.</p>
                        <Link
                            to="/contact"
                            data-testid="prise-en-charge-cta-contact"
                            className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-ink px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-brand-coal"
                        >
                            Nous contacter
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
